import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function NewsletterConfirm() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [status, setStatus] = useState('loading'); // loading, success, already_confirmed, error
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const confirmSubscription = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/newsletter/confirm/${token}`);
        setEmail(response.data.email);
        if (response.data.already_confirmed) {
          setStatus('already_confirmed');
        } else {
          setStatus('success');
        }
      } catch (err) {
        setStatus('error');
        setError(err.response?.data?.detail || t('newsletter.confirmError', 'Failed to confirm subscription'));
      }
    };

    if (token) {
      confirmSubscription();
    }
  }, [token, t]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="text-3xl font-heading font-bold text-primary">
            🧶 Even Rounds
          </Link>
        </div>

        <div className="bg-white rounded-3xl border border-stone-100 p-8 shadow-sm text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="h-16 w-16 text-primary mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-heading font-bold mb-2">
                {t('newsletter.confirming', 'Confirming your subscription...')}
              </h1>
              <p className="text-muted-foreground">
                {t('newsletter.pleaseWait', 'Please wait a moment.')}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold mb-2 text-green-700">
                {t('newsletter.confirmed', 'Subscription Confirmed!')}
              </h1>
              <p className="text-muted-foreground mb-6">
                {t('newsletter.welcomeMessage', 'Welcome to Even Rounds newsletter! You\'ll receive updates about new patterns, tips, and more.')}
              </p>
              {email && (
                <p className="text-sm text-muted-foreground mb-6">
                  <Mail className="h-4 w-4 inline mr-1" />
                  {email}
                </p>
              )}
              <div className="space-y-3">
                <Button asChild className="w-full rounded-full">
                  <Link to="/blog">{t('newsletter.exploreBlog', 'Explore Blog')}</Link>
                </Button>
                <Button asChild variant="outline" className="w-full rounded-full">
                  <Link to="/">{t('common.backToHome', 'Back to Home')}</Link>
                </Button>
              </div>
            </>
          )}

          {status === 'already_confirmed' && (
            <>
              <CheckCircle className="h-16 w-16 text-blue-500 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold mb-2 text-blue-700">
                {t('newsletter.alreadyConfirmed', 'Already Confirmed')}
              </h1>
              <p className="text-muted-foreground mb-6">
                {t('newsletter.alreadyConfirmedMessage', 'Your subscription was already confirmed. You\'re all set to receive our newsletter!')}
              </p>
              {email && (
                <p className="text-sm text-muted-foreground mb-6">
                  <Mail className="h-4 w-4 inline mr-1" />
                  {email}
                </p>
              )}
              <div className="space-y-3">
                <Button asChild className="w-full rounded-full">
                  <Link to="/blog">{t('newsletter.exploreBlog', 'Explore Blog')}</Link>
                </Button>
                <Button asChild variant="outline" className="w-full rounded-full">
                  <Link to="/">{t('common.backToHome', 'Back to Home')}</Link>
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold mb-2 text-red-700">
                {t('newsletter.confirmFailed', 'Confirmation Failed')}
              </h1>
              <p className="text-muted-foreground mb-6">
                {error || t('newsletter.invalidLink', 'This confirmation link is invalid or has expired.')}
              </p>
              <div className="space-y-3">
                <Button asChild className="w-full rounded-full">
                  <Link to="/">{t('newsletter.tryAgain', 'Subscribe Again')}</Link>
                </Button>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          {t('newsletter.questions', 'Questions?')}{' '}
          <a href="mailto:support@evenrounds.com" className="text-primary hover:underline">
            {t('newsletter.contactUs', 'Contact us')}
          </a>
        </p>
      </div>
    </div>
  );
}
